<template>
  <div class="contact-form">
    <div v-if="ticketRef">
      <div class="alert alert-success">
        <CheckIcon size="1.8x"/>
        <div>
          Thanks for contacting us! We'll take a look and get back to you as soon as we can.
        </div>
      </div>
    </div>
    <div v-else class="task-content">
      <template v-if="!noGreeting">
        <h4 v-if="userRef">
          Hi {{userRef.givenName}}, how can we help?
        </h4>
        <h4 v-else>
          How can we help?
        </h4>
      </template>
      <ContactInquiryType v-if="!inquiryType && !small" :disabled="!canEditRef" v-model="newTicket.inquiryType"/>
      <div class="options">
        <div v-if="!inquiryType && small" class="inquiry-type">
          <label class="form-label">Reason</label>
          <select class="form-select" v-model="newTicket.inquiryType" :disabled="!canEditRef">
            <option v-for="t in supportInquiryTypeInfos" :key="t.value" :value="t.value">
              {{ t.text }}
            </option>
          </select>
        </div>
        <div v-if="!priority" class="priority">
          <label class="form-label">Priority</label>
          <select class="form-select" v-model="newTicket.priority" :disabled="!canEditRef">
            <option v-for="p in orderedPriorities" :key="p.value" :value="p.value">
              <span v-if="p.iconText">{{ p.iconText }}</span>
              {{ p.text }}
            </option>
          </select>
        </div>
      </div>
      <div class="text-fields">
        <template v-if="isAnonymousRef">
          <div class="row">
            <div class="col-sm-6">
              <label for="firstName" class="form-label">First name</label>
              <input type="text" class="form-control" id="firstName" :readonly="!canEditRef" v-model="newTicket.firstName"/>
            </div>
            <div class="col-sm-6">
              <label for="lastName" class="form-label">Last name</label>
              <input type="text" class="form-control" id="lastName" :readonly="!canEditRef" v-model="newTicket.lastName"/>
            </div>
          </div>
          <div>
            <label for="email" class="form-label">Email</label>
            <input type="text" class="form-control" id="email" :readonly="!canEditRef" v-model="newTicket.email"/>
          </div>
        </template>
        <div>
          <label for="subject" class="form-label">Subject</label>
          <input type="text" class="form-control" id="subject" :readonly="!canEditRef" v-model="newTicket.subject"/>
        </div>
        <div>
          <label for="message" class="form-label">Message</label>
          <textarea class="form-control message-body" id="message" :readonly="!canEditRef" v-model="newTicket.message" :maxlength="MaxMessageLength"></textarea>
          <div class="help-text-sm">Characters remaining: {{ charsRemainingRef.toLocaleString() }}</div>
        </div>
      </div>
      <div v-if="!isAnonymousRef && !noFile" class="files">
        <label class="form-label">Attach a file (up to 50 MB)</label>
        <div class="file-selector">
          <input type="file" ref="fileInputRef" class="form-control form-control-sm" :class="{ empty: !selectedFileRef }" :disabled="!canEditRef" @change="onFileSelected" />
          <button v-if="selectedFileRef" class="btn btn-inline btn-link link-dark" type="button" @click="removeFile" :disabled="busyRef">
            <XIcon/>
          </button>
        </div>
        <div v-if="fileErrorRef" class="file-error">{{ fileErrorRef }}</div>
        <div v-if="selectedFileRef" class="help-text-sm mt-1">
          {{ ByteSizeUtil.toReadableText(selectedFileRef.size) }}
        </div>
      </div>
      <VueTurnstile v-if="isAnonymousRef && turnstileReadyRef" class="captcha" :site-key="tsKey" :appearance="captchaAppearanceRef" theme="light" v-model="captchaCodeRef"/>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.contact-form {
  min-height: 150px;
}

.title-icon {
  margin-right: 0.5rem;
  transform: translateY(-2px);
  width: 3rem;
  filter: brightness(0) saturate(100%) invert(63%) sepia(76%) saturate(5097%) hue-rotate(184deg) brightness(100%) contrast(94%);
}

.alert {
  display: flex;
  align-items: center;
  gap: 1rem;
}

h4 {
  margin: 0;
}

.task-content {
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.options {
  display: flex;
  gap: 1rem;

  select {
    width: auto;
    min-width: 150px;
  }
}

.text-fields {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

textarea {
  min-height: 120px !important; // override BS
}

.file-selector {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  input[type="file"].empty {
    color: #aaa;
  }
}

.file-error {
  color: var(--bs-danger);
  margin-top: 0.25rem;
  font-size: 0.75rem;
}

.captcha {
  margin-bottom: -1rem; // eat the flex gap
}
</style>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, reactive, watch } from 'vue'
import { useRouter } from 'vue-router'
import { usePageTitle } from '@webapp/util/AppUtil'
import { useSupportTicketStore } from '@webapp/gp/SupportTicketStore'
import { NewSupportTicket, SupportInquiryType, SupportTicket, SupportTicketPriority, supportInquiryTypeInfos } from '@webapp/gp/GroupAdminModel'
import { AddressUtil } from '@webapp/util/AddressUtil'
import { LoadMode } from '@webapp/util/AsyncData'
import { useUserStore } from '@webapp/gp/UserStore'
import { useTokenManager } from '@webapp/auth/TokenManager'
import { CheckIcon, ToolIcon, XIcon } from '@zhuowenli/vue-feather-icons'
import { AsyncUtil } from '@webapp/util/AsyncUtil'
import { Environment } from '@webapp/util/Environment'
import { ByteSizeUtil } from '@webapp/util/ByteSizeUtil'
import ContactInquiryType from './ContactInquiryType.vue'
import VueTurnstile from 'vue-turnstile'

const props = defineProps<{
  noGreeting?: boolean
  small?: boolean
  inquiryType?: SupportInquiryType
  priority?: SupportTicketPriority
  noFile?: boolean
  disabled?: boolean
  isValid: boolean
}>()

defineExpose({
  reset,
  submitTicket,
})

const emit = defineEmits([
  'update:isValid'
])

const orderedPriorities = [
  { value: SupportTicketPriority.High, text: "High", iconText: "🔥" },
  { value: SupportTicketPriority.Medium, text: "Normal", iconText: "😶" },
  { value: SupportTicketPriority.Low, text: "Low", iconText: "⬇️" },
]

const MaxMessageLength = 2000
const tsKey = Environment.get('TURNSTILE_KEY')

const router = useRouter()
const tokenManager = useTokenManager()
const userStore = useUserStore()
const supportTicketStore = useSupportTicketStore()

const busyRef = ref(false)
const newTicket = reactive(new NewSupportTicket())
const ticketRef = ref<SupportTicket>()
const selectedFileRef = ref<File>()
const captchaCodeRef = ref("")
// Turnstile must render after a nextTick delay so its container element exists in the DOM.
// Without this, Cloudflare's script fires before Vue flushes the v-if block, causing
// "Invalid type for parameter container" errors that leave the captcha broken.
const turnstileReadyRef = ref(false)
const fileInputRef = ref<HTMLInputElement>()

const userRef = computed(() => userStore.getAsyncSelf(LoadMode.EnsureLoaded)?.data)
const isAnonymousRef = computed(() => !tokenManager.current.isValid)
const charsRemainingRef = computed(() => MaxMessageLength - (newTicket.message?.length ?? 0))
const canEditRef = computed(() => !busyRef.value && !ticketRef.value && !props.disabled)
const isValidAddressRef = computed(() => AddressUtil.isValidEmail(newTicket.email))
const hasIdentityRef = computed(() => tokenManager.current.isValid || (newTicket.lastName && isValidAddressRef.value))
const captchaAppearanceRef = computed(() => Environment.isDevelopment() && router.currentRoute.value.query.forceCaptcha ? "always" : "interaction-only")
const isHumanRef = computed(() => !isAnonymousRef.value || !!captchaCodeRef.value)
const fileErrorRef = computed(() => {
  if (selectedFileRef.value && selectedFileRef.value.size > 50 * 1024 * 1024) {
    return "The selected file is too large. Consider zipping it first."
  }
  return undefined
})
const canSubmitRef = computed(() => hasIdentityRef.value && !!newTicket.subject && isHumanRef.value && !fileErrorRef.value)

usePageTitle("Contact us")

onMounted(async () => {
  await nextTick()
  turnstileReadyRef.value = true
})

function reset() {
  // reset state
  Object.assign(newTicket, new NewSupportTicket())
  newTicket.subject = new URLSearchParams(window.location.search).get('subject') ?? ''
  newTicket.inquiryType = props.inquiryType ?? SupportInquiryType.Question
  newTicket.priority = props.priority ?? SupportTicketPriority.Medium
  ticketRef.value = undefined
  captchaCodeRef.value = ""
}

function onFileSelected(e: Event) {
  const input = e.target as HTMLInputElement
  if (input.files && input.files.length > 0) {
    selectedFileRef.value = input.files[0]
  }
}

function removeFile() {
  selectedFileRef.value = undefined
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

watch(canSubmitRef, () => emit('update:isValid', canSubmitRef.value))

async function submitTicket() {
  try {
    busyRef.value = true
    newTicket.captchaCode = captchaCodeRef.value
    const ticketId = await AsyncUtil.atLeast(500, supportTicketStore.addAsync(newTicket))

    if (selectedFileRef.value) {
      await supportTicketStore.addFileAsync(ticketId, selectedFileRef.value)
    }

    ticketRef.value = supportTicketStore.asyncSupportTickets.get(ticketId)?.data // already loaded
    return ticketRef.value
  }
  finally {
    busyRef.value = false
  }
}
</script>
